import { useEffect, useRef, useState } from 'react';
import { COUNT, COURSE, PATTERNS } from '@gc/shared';
import { audio, click, strum } from '../lib/audio.js';
import { haptic } from '../lib/telegram.js';

const CHORD_CHOICES = ['Em', 'Am', 'Dm', 'C', 'G', 'D'];
/** На сколько вперёд планируем звук. Меньше — рвётся, больше — не догоняет темп. */
const LOOKAHEAD_S = 0.4;

/**
 * Тренажёр боя. Главное, чему он учит, — рука качается непрерывно, поэтому
 * «рука» на экране движется вниз-вверх на каждую восьмую, даже когда по
 * струнам не попадает: именно это движение ребёнок и должен повторить.
 */
export function StrumTrainer({ initial }: { initial?: string }): JSX.Element {
  const [pattern, setPattern] = useState(initial && PATTERNS[initial] ? initial : 'down');
  const [chord, setChord] = useState('Em');
  const [bpm, setBpm] = useState(70);
  const [metro, setMetro] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [slot, setSlot] = useState(-1);

  // Планировщик читает настройки в момент удара, поэтому темп и рисунок
  // можно менять на ходу, не останавливая бой.
  const live = useRef({ pattern, chord, bpm, metro });
  live.current = { pattern, chord, bpm, metro };

  const timers = useRef<number[]>([]);
  const loop = useRef<number | null>(null);

  function stop(): void {
    if (loop.current !== null) window.clearInterval(loop.current);
    loop.current = null;
    timers.current.forEach(window.clearTimeout);
    timers.current = [];
    setPlaying(false);
    setSlot(-1);
  }

  useEffect(() => stop, []);

  function start(): void {
    const c = audio();
    haptic.tap();
    setPlaying(true);

    let next = c.currentTime + 0.12;
    let index = 0;

    loop.current = window.setInterval(() => {
      const now = c.currentTime;
      const step = 30 / live.current.bpm; // длительность восьмой

      while (next < now + LOOKAHEAD_S) {
        const i = index % 8;
        const stroke = PATTERNS[live.current.pattern]!.slots[i]!;
        if (stroke) strum(live.current.chord, stroke === 'U', next);
        if (live.current.metro && i % 2 === 0) click(next, i === 0);

        const delay = Math.max(0, (next - now) * 1000);
        timers.current.push(window.setTimeout(() => setSlot(i), delay));

        next += step;
        index++;
      }

      // Бой без остановки утомляет: через 64 такта останавливаемся сами.
      if (index > 8 * 64) stop();
    }, 70);
  }

  const slots = PATTERNS[pattern]!.slots;
  const step = 30 / bpm;

  return (
    <div className="card">
      <div className="hand-lane">
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="lane-string" style={{ top: `${14 + i * 13}%` }} />
        ))}
        <svg
          className="hand"
          viewBox="0 0 54 60"
          aria-hidden="true"
          style={{
            transitionDuration: `${step}s`,
            transform:
              slot < 0
                ? 'translateY(0) rotate(0deg)'
                : `translateY(${slot % 2 ? -24 : 24}px) rotate(${slot % 2 ? -8 : 8}deg)`,
          }}
        >
          <path d="M16 6 L34 2 L40 30 L34 52 Q27 60 20 52 L14 32 Z" />
          <path className="hand-line" d="M20 12 L30 9" />
        </svg>
      </div>

      <div className="beats">
        {slots.map((s, i) => (
          <div key={i} className={`beat ${s ? '' : 'empty'} ${slot === i ? 'now' : ''}`}>
            <span className="arrow">{s === 'U' ? '↑' : '↓'}</span>
            <span className="count">{COUNT[i]}</span>
          </div>
        ))}
      </div>

      <p className="hint">{COURSE.strumming[PATTERNS[pattern]!.courseKey]}</p>

      <div className="row">
        <span className="field">Рисунок</span>
        <div className="chips">
          {Object.entries(PATTERNS).map(([key, p]) => (
            <button
              key={key}
              type="button"
              className="chip"
              aria-pressed={key === pattern}
              onClick={() => setPattern(key)}
            >
              {p.name}
            </button>
          ))}
        </div>
      </div>

      <div className="row">
        <span className="field">Аккорд</span>
        <div className="chips">
          {CHORD_CHOICES.map((c) => (
            <button
              key={c}
              type="button"
              className="chip ghost"
              aria-pressed={c === chord}
              onClick={() => setChord(c)}
            >
              {c}
            </button>
          ))}
        </div>
      </div>

      <div className="row">
        <span className="field">Темп</span>
        <div className="tempo">
          <input
            type="range"
            min={45}
            max={130}
            value={bpm}
            onChange={(e) => setBpm(Number(e.target.value))}
          />
          <span className="mono">{bpm} bpm</span>
        </div>
      </div>

      <div className="row buttons">
        <button type="button" className="btn" onClick={playing ? stop : start}>
          {playing ? '■ Стоп' : '▶ Играть'}
        </button>
        <button
          type="button"
          className="chip ghost"
          aria-pressed={metro}
          onClick={() => setMetro((v) => !v)}
        >
          Метроном
        </button>
      </div>
    </div>
  );
}
